/**
 * 防抖函數 - 限制函數在短時間內只執行最後一次
 *
 * @param {Function} func - 要執行的函數
 * @param {number} wait - 延遲時間（毫秒）
 * @param {boolean} immediate - 是否在觸發事件後立即執行
 * @returns {Function} 返回一個經過防抖處理的函數
 */
export function debounce(func, wait = 300, immediate = false) {
    let timeout;

    return function executedFunction(...args) {
        const context = this;

        const later = function () {
            timeout = null;
            if (!immediate) func.apply(context, args);
        };

        const callNow = immediate && !timeout;

        clearTimeout(timeout);
        timeout = setTimeout(later, wait);

        if (callNow) func.apply(context, args);
    };
}

/**
 * 節流函數 - 限制函數在一定時間內只執行一次
 *
 * @param {Function} func - 要執行的函數
 * @param {number} limit - 時間限制（毫秒）
 * @returns {Function} 返回一個經過節流處理的函數
 */
export function throttle(func, limit = 300) {
    let inThrottle;

    return function (...args) {
        const context = this;

        if (!inThrottle) {
            func.apply(context, args);
            inThrottle = true;
            setTimeout(() => (inThrottle = false), limit);
        }
    };
}

/**
 * 深拷貝函數 - 創建對象的完整副本
 *
 * @param {*} obj - 要複製的對象
 * @returns {*} 返回一個新的對象
 */
export function deepClone(obj) {
    if (obj === null || typeof obj !== "object") {
        return obj;
    }

    if (obj instanceof Date) {
        return new Date(obj.getTime());
    }

    if (obj instanceof Array) {
        return obj.map((item) => deepClone(item));
    }

    if (obj instanceof Object) {
        const copy = {};
        Object.keys(obj).forEach((key) => {
            copy[key] = deepClone(obj[key]);
        });
        return copy;
    }

    throw new Error("Unable to copy obj! Its type isn't supported.");
}

/**
 * 格式化日期
 *
 * @param {Date|string|number} date - 日期對象、時間戳或日期字符串
 * @param {string} format - 格式化模板，如 'YYYY-MM-DD HH:mm:ss'
 * @returns {string} 返回格式化後的日期字符串
 */
export function formatDate(date, format = "YYYY-MM-DD") {
    const d = new Date(date);

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const seconds = String(d.getSeconds()).padStart(2, "0");

    return format
        .replace("YYYY", year)
        .replace("MM", month)
        .replace("DD", day)
        .replace("HH", hours)
        .replace("mm", minutes)
        .replace("ss", seconds);
}

/**
 * 生成唯一ID
 *
 * @param {number} length - ID長度
 * @returns {string} 返回生成的唯一ID
 */
export function generateId(length = 8) {
    const chars =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";

    for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    return result;
}

/**
 * 檢查是否為空值
 *
 * @param {*} value - 要檢查的值
 * @returns {boolean} 返回是否為空
 */
export function isEmpty(value) {
    if (value === null || value === undefined) {
        return true;
    }

    if (typeof value === "string" && value.trim() === "") {
        return true;
    }

    if (Array.isArray(value) && value.length === 0) {
        return true;
    }

    if (typeof value === "object" && Object.keys(value).length === 0) {
        return true;
    }

    return false;
}

export function getTextWithoutSign(text) {
    if (!text) return "";
    return text.replace(/（[^）]*）/g, "");
}

export function convertToChineseFormatNative(dateString) {
    // Chinese month names
    const chineseMonths = [
        "1月",
        "2月",
        "3月",
        "4月",
        "5月",
        "6月",
        "7月",
        "8月",
        "9月",
        "10月",
        "11月",
        "12月",
    ];

    // Chinese weekday names
    const chineseWeekdays = [
        "週日",
        "週一",
        "週二",
        "週三",
        "週四",
        "週五",
        "週六",
    ];

    // Parse the date string
    const date = new Date(dateString);

    // Extract components
    const month = chineseMonths[date.getMonth()];
    const day = date.getDate() + "日";
    const weekday = chineseWeekdays[date.getDay()];
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");

    // Format the result
    return `${month}${day}(${weekday}) ${hours}:${minutes}`;
}

export function transformPresentImageUrl(
    url,
    { targetWidth = null, targetHeight = null } = {}
) {
    if (!url || !/\/image\/present\//i.test(url)) {
        return url;
    }

    const segments = url.split("/");
    const imageIndex = segments.findIndex(
        (segment) => segment.toLowerCase() === "image"
    );

    if (
        imageIndex === -1 ||
        segments[imageIndex + 1]?.toLowerCase() !== "present"
    ) {
        return url;
    }

    const originalWidth = Number(segments[imageIndex + 3]);
    const originalHeight = Number(segments[imageIndex + 4]);

    let scale = null;
    if (targetWidth != null && originalWidth) {
        scale = targetWidth / originalWidth;
    } else if (targetHeight != null && originalHeight) {
        scale = targetHeight / originalHeight;
    }

    if (!scale) {
        return url;
    }

    const formatNumber = (value) => {
        if (Number.isInteger(value)) {
            return String(value);
        }
        return value.toFixed(6).replace(/\.?0+$/, "");
    };

    const nextSegments = [...segments];
    for (let index = imageIndex + 3; index < nextSegments.length; index += 1) {
        const parsedValue = Number(nextSegments[index]);
        if (Number.isNaN(parsedValue)) {
            return url;
        }
        nextSegments[index] = formatNumber(parsedValue * scale);
    }

    return nextSegments.join("/");
}
